/**
 * Blood Pressure Medical Calculations & Statistical Aggregations
 * Implements AHA / WHO clinical metrics: MAP, Pulse Pressure, Daily Summaries & Fluctuations.
 */

import {
  BloodPressureRecord,
  DailySummary,
  BPStatistics,
  BPCategory,
} from '@/types/record';
import { classifyBP } from './bpClassification';

/**
 * Calculate Mean Arterial Pressure (平均動脈壓 MAP)
 * Formula: DBP + (SBP - DBP) / 3
 * Normal clinical range is approximately 70 to 100 mmHg.
 * Returns value rounded to 1 decimal place.
 */
export function calculateMAP(systolic: number, diastolic: number): number {
  if (isNaN(systolic) || isNaN(diastolic)) return 0;
  const pp = systolic - diastolic;
  const map = diastolic + pp / 3;
  return Math.round(map * 10) / 10;
}

/**
 * Calculate Pulse Pressure (脈壓差)
 * Formula: Systolic - Diastolic
 * Normal clinical range is 30 to 50 mmHg. Values > 60 mmHg indicate arterial stiffness.
 */
export function calculatePulsePressure(systolic: number, diastolic: number): number {
  if (isNaN(systolic) || isNaN(diastolic)) return 0;
  return systolic - diastolic;
}

/**
 * Calculate Blood Pressure & Pulse Fluctuation across a set of records
 */
export function calculateFluctuation(records: BloodPressureRecord[]): {
  count: number;
  minSystolic: number;
  maxSystolic: number;
  systolicFluctuation: number;
  minDiastolic: number;
  maxDiastolic: number;
  diastolicFluctuation: number;
  minPulse: number;
  maxPulse: number;
  pulseFluctuation: number;
} {
  if (!records || records.length === 0) {
    return {
      count: 0,
      minSystolic: 0,
      maxSystolic: 0,
      systolicFluctuation: 0,
      minDiastolic: 0,
      maxDiastolic: 0,
      diastolicFluctuation: 0,
      minPulse: 0,
      maxPulse: 0,
      pulseFluctuation: 0,
    };
  }

  let minSystolic = Infinity;
  let maxSystolic = -Infinity;
  let minDiastolic = Infinity;
  let maxDiastolic = -Infinity;
  let minPulse = Infinity;
  let maxPulse = -Infinity;

  for (const r of records) {
    if (r.systolic < minSystolic) minSystolic = r.systolic;
    if (r.systolic > maxSystolic) maxSystolic = r.systolic;
    if (r.diastolic < minDiastolic) minDiastolic = r.diastolic;
    if (r.diastolic > maxDiastolic) maxDiastolic = r.diastolic;
    if (r.pulse < minPulse) minPulse = r.pulse;
    if (r.pulse > maxPulse) maxPulse = r.pulse;
  }

  return {
    count: records.length,
    minSystolic: isFinite(minSystolic) ? minSystolic : 0,
    maxSystolic: isFinite(maxSystolic) ? maxSystolic : 0,
    systolicFluctuation: isFinite(maxSystolic) && isFinite(minSystolic) ? maxSystolic - minSystolic : 0,
    minDiastolic: isFinite(minDiastolic) ? minDiastolic : 0,
    maxDiastolic: isFinite(maxDiastolic) ? maxDiastolic : 0,
    diastolicFluctuation: isFinite(maxDiastolic) && isFinite(minDiastolic) ? maxDiastolic - minDiastolic : 0,
    minPulse: isFinite(minPulse) ? minPulse : 0,
    maxPulse: isFinite(maxPulse) ? maxPulse : 0,
    pulseFluctuation: isFinite(maxPulse) && isFinite(minPulse) ? maxPulse - minPulse : 0,
  };
}

/**
 * Calculate Daily Summary from a collection of records for a specific date
 */
export function calculateDailySummary(
  records: BloodPressureRecord[],
  targetDate?: string
): DailySummary {
  const date = targetDate || records[0]?.measured_date || new Date().toISOString().slice(0, 10);

  if (!records || records.length === 0) {
    return {
      date,
      count: 0,
      avg_systolic: 0,
      avg_diastolic: 0,
      avg_pulse: 0,
      min_systolic: 0,
      max_systolic: 0,
      min_diastolic: 0,
      max_diastolic: 0,
      fluctuation_systolic: 0,
      fluctuation_diastolic: 0,
      avg_pulse_pressure: 0,
      avg_map: 0,
      category: 'NORMAL',
    };
  }

  const count = records.length;
  const sumSys = records.reduce((sum, r) => sum + r.systolic, 0);
  const sumDia = records.reduce((sum, r) => sum + r.diastolic, 0);
  const sumPulse = records.reduce((sum, r) => sum + r.pulse, 0);

  const avg_systolic = Math.round((sumSys / count) * 10) / 10;
  const avg_diastolic = Math.round((sumDia / count) * 10) / 10;
  const avg_pulse = Math.round((sumPulse / count) * 10) / 10;

  const fluc = calculateFluctuation(records);
  const avg_pulse_pressure = Math.round((avg_systolic - avg_diastolic) * 10) / 10;
  const avg_map = calculateMAP(avg_systolic, avg_diastolic);
  const categoryMeta = classifyBP(avg_systolic, avg_diastolic);

  return {
    date,
    count,
    avg_systolic,
    avg_diastolic,
    avg_pulse,
    min_systolic: fluc.minSystolic,
    max_systolic: fluc.maxSystolic,
    min_diastolic: fluc.minDiastolic,
    max_diastolic: fluc.maxDiastolic,
    fluctuation_systolic: fluc.systolicFluctuation,
    fluctuation_diastolic: fluc.diastolicFluctuation,
    avg_pulse_pressure,
    avg_map,
    category: categoryMeta.category,
  };
}

/**
 * Calculate overall comprehensive statistics across multiple days and records
 */
export function calculateOverallStatistics(records: BloodPressureRecord[]): BPStatistics {
  if (!records || records.length === 0) {
    return {
      totalRecords: 0,
      totalDays: 0,
      overallAvgSystolic: 0,
      overallAvgDiastolic: 0,
      overallAvgPulse: 0,
      overallPulsePressure: 0,
      overallMAP: 0,
      categoryCounts: {
        NORMAL: 0,
        ELEVATED: 0,
        STAGE_1: 0,
        STAGE_2: 0,
        CRISIS: 0,
      },
    };
  }

  const totalRecords = records.length;
  const uniqueDates = new Set(records.map((r) => r.measured_date));
  const totalDays = uniqueDates.size;

  let totalSys = 0;
  let totalDia = 0;
  let totalPulse = 0;

  let morningSys = 0;
  let morningDia = 0;
  let morningCount = 0;

  let eveningSys = 0;
  let eveningDia = 0;
  let eveningCount = 0;

  const categoryCounts: Record<BPCategory, number> = {
    NORMAL: 0,
    ELEVATED: 0,
    STAGE_1: 0,
    STAGE_2: 0,
    CRISIS: 0,
  };

  // Find latest record by measured_at
  let latestRecord: BloodPressureRecord | undefined = records[0];

  for (const r of records) {
    totalSys += r.systolic;
    totalDia += r.diastolic;
    totalPulse += r.pulse;

    const cat = classifyBP(r.systolic, r.diastolic).category;
    categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;

    // Morning segment: measured_time < 12:00
    if (r.measured_time < '12:00') {
      morningSys += r.systolic;
      morningDia += r.diastolic;
      morningCount++;
    }

    // Evening segment: measured_time >= 18:00
    if (r.measured_time >= '18:00') {
      eveningSys += r.systolic;
      eveningDia += r.diastolic;
      eveningCount++;
    }

    if (!latestRecord || r.measured_at > latestRecord.measured_at) {
      latestRecord = r;
    }
  }

  const overallAvgSystolic = Math.round((totalSys / totalRecords) * 10) / 10;
  const overallAvgDiastolic = Math.round((totalDia / totalRecords) * 10) / 10;
  const overallAvgPulse = Math.round((totalPulse / totalRecords) * 10) / 10;
  const overallPulsePressure = Math.round((overallAvgSystolic - overallAvgDiastolic) * 10) / 10;
  const overallMAP = calculateMAP(overallAvgSystolic, overallAvgDiastolic);

  const normalAndElevatedCount = (categoryCounts.NORMAL || 0) + (categoryCounts.ELEVATED || 0);
  const targetAttainmentRate = totalRecords > 0 ? Math.round((normalAndElevatedCount / totalRecords) * 1000) / 10 : 0;

  return {
    totalRecords,
    totalDays,
    overallAvgSystolic,
    overallAvgDiastolic,
    overallAvgPulse,
    overallPulsePressure,
    overallMAP,
    morningAvgSystolic: morningCount > 0 ? Math.round((morningSys / morningCount) * 10) / 10 : undefined,
    morningAvgDiastolic: morningCount > 0 ? Math.round((morningDia / morningCount) * 10) / 10 : undefined,
    eveningAvgSystolic: eveningCount > 0 ? Math.round((eveningSys / eveningCount) * 10) / 10 : undefined,
    eveningAvgDiastolic: eveningCount > 0 ? Math.round((eveningDia / eveningCount) * 10) / 10 : undefined,
    targetAttainmentRate,
    categoryCounts,
    latestRecord,
  };
}

/**
 * Clinical assessment helper for Pulse Pressure
 */
export function getPulsePressureEvaluation(pp: number): {
  labelZh: string;
  isHigh: boolean;
  isLow: boolean;
  description: string;
} {
  if (pp > 60) {
    return {
      labelZh: '脈壓差過大',
      isHigh: true,
      isLow: false,
      description: '脈壓差 > 60 mmHg，可能提示動脈彈性降低或硬化，建議諮詢醫師。',
    };
  }
  if (pp < 30) {
    return {
      labelZh: '脈壓差過小',
      isHigh: false,
      isLow: true,
      description: '脈壓差 < 30 mmHg，可能提示心輸出量降低或心包填塞等徵兆。',
    };
  }
  return {
    labelZh: '脈壓差正常',
    isHigh: false,
    isLow: false,
    description: '脈壓差介於 30 至 60 mmHg 之間，血管彈性處於理想範圍。',
  };
}

/**
 * Clinical assessment helper for Blood Pressure Fluctuation
 */
export function getFluctuationEvaluation(sysFluctuation: number): {
  status: 'stable' | 'moderate' | 'high';
  labelZh: string;
  colorClass: string;
  description: string;
} {
  if (sysFluctuation > 25) {
    return {
      status: 'high',
      labelZh: '波動顯著',
      colorClass: 'text-red-700 bg-red-50 border-red-300',
      description: '單日收縮壓波動超過 25 mmHg，建議注意情緒、作息及按時服藥。',
    };
  }
  if (sysFluctuation > 15) {
    return {
      status: 'moderate',
      labelZh: '輕度波動',
      colorClass: 'text-amber-800 bg-amber-50 border-amber-300',
      description: '單日收縮壓有正常生理波動 (15~25 mmHg)。',
    };
  }
  return {
    status: 'stable',
    labelZh: '波動平穩',
    colorClass: 'text-emerald-800 bg-emerald-50 border-emerald-300',
    description: '單日血壓維持在穩定範圍內 (波動 ≤ 15 mmHg)。',
  };
}
